import axios from "axios";
import { ModalContext } from "app/app";
import { Fragment, useContext, useState } from 'react';
import { Form, Formik } from 'formik';
import { FieldInput } from "components/fieldInput";
import { ViewContext } from "components/lib";
import * as Yup from "yup";
import styles from "./styles.module.scss";

const validationSchema = Yup.object().shape({
  company_name: Yup.string().required('Company name is required!'),
  owner_name: Yup.string().required('Owner name is required!'),
  phone: Yup.string().required('Phone number is required!'),
  email: Yup.string().email('Invalid email!').required('Email is required!'),
  dot_number: Yup.string(),
  dba_name: Yup.string(),
  address: Yup.string(),
  city: Yup.string(),
  state: Yup.string(),
  zip: Yup.string(),
});

export function CompanyModal() {
  const context = useContext(ViewContext);
  const { modal, setModal } = useContext(ModalContext);
  const [loading, setLoading] = useState(false);

  const handleFormSubmit = async (values) => {
    setLoading(true);
    try {
      const response = await axios.post("/api/company", {
        dot_number: values.dot_number || "",
        name: values.company_name.trim(),
        dba_name: values.dba_name || "",
        owner_name: values.owner_name.trim(),
        phone: values.phone,
        email: values.email.trim(),
        premium: 0,
        status: 'lead',
        address: values.address || "",
        city: values.city || "",
        state: values.state || "",
        zip: values.zip || "",
      });

      setLoading(false);

      context.notification.show(
        "Company created successfully!",
        "success",
        false,
        "toast",
        "check-circle"
      );

      if (response.data && response.data.data && response.data.data.id) {
        // Pass the newly created company data to the action callback
        const newCompanyData = {
          id: response.data.data.id,
          name: values.company_name.trim(),
          company_name: values.company_name.trim(),
          email: values.email.trim(),
          ...response.data.data
        };
        if (modal.action) {
          modal.action(newCompanyData);
        } else {
          // Only close modal if there's no action callback
          setModal("");
        }
      } else {
        setModal("");  // Close modal in error case
        console.error("Missing ID in response:", response.data);
        context.notification.show(
          "Company created, but there might be an issue. Please verify the company was created.",
          "warning",
          false,
          "toast",
          "alert-circle"
        );
      }
    } catch (error) {
      console.error("API Error:", error.response?.data || error);
      setLoading(false);
      setModal("");

      const errorMessage = error.response?.data?.message ||
        "An error occurred while creating the company";

      context.notification.show(
        errorMessage,
        "error",
        true,
        "toast",
        "x-circle"
      );
    }
  };

  return (
    <Fragment>
      {modal.name === "company_modal" && (
        <div className={styles.background}>
          <div className={styles.wrapper}>
            <div className={styles.insideWrapper}>
              <div className={styles.title}>Create Company</div>
              <div className={styles.line} />
              <Formik
                validationSchema={validationSchema}
                initialValues={{
                  company_name: "",
                  dba_name: "",
                  owner_name: "",
                  phone: "",
                  email: "",
                  dot_number: "",
                  address: "",
                  city: "",
                  state: "",
                  zip: "",
                }}
                onSubmit={handleFormSubmit}
              >
                {({
                  errors,
                  touched,
                  handleSubmit,
                  handleChange,
                  handleBlur,
                  submitForm,
                }) => (
                  <Fragment>
                    <Form className={styles.form} onSubmit={handleSubmit}>
                      <div className={styles.fieldRow}>
                        <FieldInput
                          id="company_name"
                          type="text"
                          label="Company Name"
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          required
                          showErrors
                          errors={errors.company_name}
                          touched={touched.company_name}
                        />
                        <FieldInput
                          id="dba_name"
                          type="text"
                          label="DBA Name"
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          showErrors
                          errors={errors.dba_name}
                          touched={touched.dba_name}
                        />
                      </div>
                      <div className={styles.fieldRow}>
                        <FieldInput
                          id="owner_name"
                          type="text"
                          label="Owner Name"
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          required
                          showErrors
                          errors={errors.owner_name}
                          touched={touched.owner_name}
                        />
                        <FieldInput
                          id="dot_number"
                          type="text"
                          label="DOT Number"
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          showErrors
                          errors={errors.dot_number}
                          touched={touched.dot_number}
                        />
                      </div>
                      <div className={styles.fieldRow}>
                        <FieldInput
                          id="phone"
                          type="text"
                          label="Phone"
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          required
                          showErrors
                          errors={errors.phone}
                          touched={touched.phone}
                        />
                        <FieldInput
                          id="email"
                          type="text"
                          label="Email"
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          required
                          showErrors
                          errors={errors.email}
                          touched={touched.email}
                        />
                      </div>
                      <div className={styles.fieldRow}>
                        <FieldInput
                          id="address"
                          type="text"
                          label="Address"
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          showErrors
                          errors={errors.address}
                          touched={touched.address}
                        />
                      </div>
                      <div className={styles.fieldRow}>
                        <FieldInput
                          id="city"
                          type="text"
                          label="City"
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          showErrors
                          errors={errors.city}
                          touched={touched.city}
                        />
                        <FieldInput
                          id="state"
                          type="text"
                          label="State"
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          showErrors
                          errors={errors.state}
                          touched={touched.state}
                        />
                      </div>
                      <div className={styles.fieldRow}>
                        <FieldInput
                          id="zip"
                          type="text"
                          label="ZIP Code"
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          showErrors
                          errors={errors.zip}
                          touched={touched.zip}
                        />
                      </div>
                    </Form>
                    <div className={styles.footer}>
                      <div className={styles.buttons}>
                        <button
                          className={styles.cancel}
                          onClick={() => setModal("")}
                          type="button"
                        >
                          Cancel
                        </button>
                        <button
                          className={styles.create}
                          onClick={submitForm}
                          type="button"
                          disabled={loading}
                        >
                          {loading ? "Creating..." : "Create"}
                        </button>
                      </div>
                    </div>
                  </Fragment>
                )}
              </Formik>
            </div>
          </div>
        </div>
      )}
    </Fragment>
  );
}